// ─────────────────────────────────────────────────────────
// Einstellungen → Lohngruppen (Kalkulation, plancraft-Stil)
// ─────────────────────────────────────────────────────────
// Eigenes Modul, damit CompanyDashboard.js nicht weiter wächst.
// Deutsche UI. Nutzt /api/wage-groups (org-scoped, wages:read/write).
// Gespeichert werden nur die Eingaben (Stundenlohn + Kosten + Zuschlag%).
// Abgeleitete Werte (Lohnkosten EK, Stundenverrechnungssatz, CHF/min) werden
// live direkt am DOM berechnet (recalcWageGroup) — kein draw(), damit Eingaben
// beim Tippen nicht zurückgesetzt werden.
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
import { icon, escapeHtml } from '../../utils/dom.js';
import { createWageGroup, updateWageGroup, deleteWageGroup } from '../../core/org.js';
import { amount } from '../../utils/format.ts';
import { t } from '../../i18n/index.js';

// ── Formatierung (Schweizer CHF) — zentral aus utils/format.ts
const fmtH = (n) => amount(n);
const fmtMin = (n) => amount((Number(n) || 0) / 60, { decimals: 4 });

// ── Berechnung (identisch zum Backend) ────────────────────
function compute(v) {
  const ek = (Number(v.stundenlohn) || 0) + (Number(v.lohngebundeneKosten) || 0) + (Number(v.gemeinkosten) || 0);
  const zuschlagBetrag = (ek * (Number(v.zuschlagProzent) || 0)) / 100;
  return { ek, zuschlagBetrag, vs: ek + zuschlagBetrag };
}

function memberAvatars(members, ctx) {
  const { grad, initials } = ctx;
  if (!members || !members.length) return '<span class="cd-wtm-none">–</span>';
  const shown = members.slice(0, 5);
  const rest = members.length - shown.length;
  const av = shown
    .map((m) => `<span class="cd-wtm-av" title="${escapeHtml(m.name)}" style="background:${grad(m.name)}">${escapeHtml(initials(m.name, ''))}</span>`)
    .join('');
  return `<div class="cd-wtm-avstack">${av}${rest > 0 ? `<span class="cd-wtm-av cd-wtm-av-more">+${rest}</span>` : ''}</div>`;
}

// „Freigeschaltet für"-Text
function freigeschaltetLabel(g) {
  const parts = [];
  if (g.enablePreiskalkulation) parts.push(t('settings.wageGroups.labelKalkulation'));
  if (g.enableNachkalkulation) parts.push(t('settings.wageGroups.enableNachkalkulation'));
  return parts.length ? parts.join(', ') : '–';
}

// ── Listen-Bereich ────────────────────────────────────────
export function renderWageGroupsSection(ctx) {
  const { s, can } = ctx;
  if (!can('wages:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.wgLoading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const groups = s.wageGroups || [];
  const info = `
    <div class="cd-emp-info">${icon('info', 18)}<span>${escapeHtml(t('settings.wageGroups.info'))}</span></div>`;

  let body;
  if (!groups.length) {
    body = `<div class="cd-emp-empty">${escapeHtml(t('settings.wageGroups.empty'))}</div>`;
  } else {
    const rows = groups
      .map((g) => {
        const clickable = can('wages:write');
        const clickAttr = clickable ? ` data-action="wg-edit-open" data-id="${escapeHtml(g.id)}"` : '';
        const badge = g.isDefault ? ` <span class="cd-wtm-badge">${escapeHtml(t('settings.wageGroups.defaultBadge'))}</span>` : '';
        return `
          <div class="cd-wg-trow${clickable ? ' cd-emp-trow-click' : ''}"${clickAttr}>
            <div class="cd-wtm-name">${icon('euro', 18)}<span>${escapeHtml(g.name)}${badge}</span></div>
            <div class="cd-wg-num">${fmtH(g.lohnkostenEk)}</div>
            <div class="cd-wg-num">${fmtH(g.stundenverrechnungssatz)}</div>
            <div class="cd-emp-mail">${escapeHtml(freigeschaltetLabel(g))}</div>
            <div>${memberAvatars(g.members, ctx)}</div>
          </div>`;
      })
      .join('');
    body = `
      <div class="cd-emp-table">
        <div class="cd-wg-thead">
          <span>${escapeHtml(t('settings.wageGroups.colName'))}</span><span>${escapeHtml(t('settings.wageGroups.colEk'))}</span><span>${escapeHtml(t('settings.wageGroups.colVs'))}</span><span>${escapeHtml(t('settings.wageGroups.colEnabled'))}</span><span>${escapeHtml(t('settings.wageGroups.colMembers'))}</span>
        </div>
        ${rows}
      </div>`;
  }

  return `
    <div class="cd-emp">
      ${info}
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('euro', 18)}<h3>${escapeHtml(t('settings.wageGroups.title'))}</h3></div>
        ${body}
      </div>
    </div>`;
}

export function wageGroupsHeadButton(ctx) {
  if (!ctx.can('wages:write')) return '';
  return `<button class="cd-btn-primary" data-action="wg-create-open">${icon('add', 16)}${escapeHtml(t('settings.wageGroups.create'))}</button>`;
}

// ── Modal (Erstellen / Bearbeiten) ────────────────────────
export function renderWageGroupModal(ctx) {
  const { s } = ctx;
  const e = s.wgEdit;
  if (!e || !e.open) return '';
  const dis = e.saving ? 'disabled' : '';
  const isEdit = !!e.id;
  const members = s.members || [];
  const c = compute(e);

  const memberChips = members.length
    ? members
        .map((m) => {
          const label = m.name || m.email;
          const checked = e.memberIds.includes(m.id) ? 'checked' : '';
          return `
            <label class="cd-wtm-mchip">
              <input type="checkbox" data-wg-member value="${escapeHtml(m.id)}" ${checked} ${dis}>
              <span>${escapeHtml(label)}</span>
            </label>`;
        })
        .join('')
    : `<div class="cd-wtm-none">${escapeHtml(t('settings.wageGroups.noMembers'))}</div>`;

  // Zahlen-Eingabe (CHF/h) — editierbar, weiss
  const moneyIn = (id, val) =>
    `<input type="number" step="0.01" min="0" class="cd-input cd-wg-in" id="${id}" data-wg-recalc value="${Number(val) || 0}" ${dis}>`;
  // Berechnete Zelle — grau, readonly
  const cell = (id, txt) => `<div class="cd-wg-cell" id="${id}">${txt}</div>`;

  const calcRows = `
    <div class="cd-wg-crow">
      <span>${escapeHtml(t('settings.wageGroups.stundenlohn'))}</span>
      ${moneyIn('wg-stundenlohn', e.stundenlohn)}
      ${cell('wg-stundenlohn-min', fmtMin(e.stundenlohn))}
      <span></span>
    </div>
    <div class="cd-wg-crow">
      <span>${escapeHtml(t('settings.wageGroups.lohngebundeneKosten'))}</span>
      ${moneyIn('wg-lohngebundene', e.lohngebundeneKosten)}
      ${cell('wg-lohngebundene-min', fmtMin(e.lohngebundeneKosten))}
      <span></span>
    </div>
    <div class="cd-wg-crow">
      <span>${escapeHtml(t('settings.wageGroups.gemeinkosten'))}</span>
      ${moneyIn('wg-gemeinkosten', e.gemeinkosten)}
      ${cell('wg-gemeinkosten-min', fmtMin(e.gemeinkosten))}
      <span></span>
    </div>
    <div class="cd-wg-crow cd-wg-sum">
      <span>${escapeHtml(t('settings.wageGroups.lohnkostenEk'))}</span>
      ${cell('wg-ek-h', fmtH(c.ek))}
      ${cell('wg-ek-min', fmtMin(c.ek))}
      <span></span>
    </div>
    <div class="cd-wg-crow">
      <span>${escapeHtml(t('settings.wageGroups.zuschlag'))}</span>
      ${cell('wg-zuschlag-h', fmtH(c.zuschlagBetrag))}
      ${cell('wg-zuschlag-min', fmtMin(c.zuschlagBetrag))}
      <div class="cd-wg-pct"><input type="number" step="0.01" min="0" class="cd-input cd-wg-in" id="wg-zuschlag-pct" data-wg-recalc value="${Number(e.zuschlagProzent) || 0}" ${dis}><span>%</span></div>
    </div>
    <div class="cd-wg-crow cd-wg-total">
      <span>${escapeHtml(t('settings.wageGroups.stundenverrechnungssatz'))}</span>
      ${cell('wg-vs-h', fmtH(c.vs))}
      ${cell('wg-vs-min', fmtMin(c.vs))}
      <span></span>
    </div>`;

  const footBtns = isEdit
    ? (e.confirmDelete
        ? `<div class="cd-wtm-confirm"><span>${escapeHtml(t('settings.wageGroups.confirmDeleteQuestion'))}</span>
             <button class="cd-btn-ghost" data-action="wg-delete-cancel" ${dis}>${escapeHtml(t('common.cancel'))}</button>
             <button class="cd-btn-danger" data-action="wg-delete-confirm" ${dis}>${escapeHtml(e.saving ? t('common.deleting') : t('common.confirmDelete'))}</button></div>`
        : `<button class="cd-btn-danger-ghost" data-action="wg-delete" ${dis}>${icon('delete', 16)}${escapeHtml(t('settings.wageGroups.delete'))}</button>
           <div class="cd-wtm-foot-spacer"></div>
           <button class="cd-btn-ghost" data-action="wg-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
           <button class="cd-btn-primary" data-action="wg-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>`)
    : `<div class="cd-wtm-foot-spacer"></div>
       <button class="cd-btn-ghost" data-action="wg-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
       <button class="cd-btn-primary" data-action="wg-submit" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>`;

  return `
    <div class="cd-emp-modal-overlay" data-action="wg-close">
      <div class="cd-emp-modal cd-wg-modal" data-action="wg-noop">
        <div class="cd-emp-modal-head">
          <h2>${icon('euro', 20)}${escapeHtml(isEdit ? t('settings.wageGroups.editTitle') : t('settings.wageGroups.create'))}</h2>
          <button class="cd-emp-modal-x" data-action="wg-close" title="${escapeHtml(t('common.close'))}">${icon('close', 20)}</button>
        </div>
        <div class="cd-emp-modal-body">
          ${e.error ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(e.error)}</span></div>` : ''}
          <label class="cd-field"><span>${escapeHtml(t('settings.wageGroups.nameLabel'))}</span><input class="cd-input" id="wg-name" type="text" placeholder="${escapeHtml(t('common.name'))}" value="${escapeHtml(e.name || '')}" ${dis}></label>

          <div class="cd-form-section">${escapeHtml(t('settings.wageGroups.sectionMembers'))}</div>
          <div class="cd-wtm-mlist">${memberChips}</div>

          <div class="cd-form-section">${escapeHtml(t('settings.wageGroups.sectionEnable'))}</div>
          <div class="cd-wg-freischalten">
            <label class="cd-wg-toggle">
              <input type="checkbox" id="wg-preis" ${e.enablePreiskalkulation ? 'checked' : ''} ${dis}>
              <span>${escapeHtml(t('settings.wageGroups.enablePreiskalkulation'))} <em>${escapeHtml(t('settings.wageGroups.enablePreiskalkulationHint'))}</em></span>
            </label>
            <label class="cd-wg-default-sub">
              <input type="checkbox" id="wg-default" ${e.isDefault ? 'checked' : ''} ${dis}>
              <span>${escapeHtml(t('settings.wageGroups.setDefault'))}</span>
            </label>
            <label class="cd-wg-toggle">
              <input type="checkbox" id="wg-nach" ${e.enableNachkalkulation ? 'checked' : ''} ${dis}>
              <span>${escapeHtml(t('settings.wageGroups.enableNachkalkulation'))}</span>
            </label>
          </div>

          <div class="cd-form-section">${escapeHtml(t('settings.wageGroups.sectionCalc'))}</div>
          <div class="cd-wg-calc">
            <div class="cd-wg-chead"><span></span><span>${escapeHtml(t('settings.wageGroups.headPerHour'))}</span><span>${escapeHtml(t('settings.wageGroups.headPerMin'))}</span><span></span></div>
            ${calcRows}
          </div>
        </div>
        <div class="cd-emp-modal-foot">${footBtns}</div>
      </div>
    </div>`;
}

// ── Live-Neuberechnung (direkt am DOM, ohne draw) ─────────
export function recalcWageGroup() {
  const read = (id) => parseFloat(document.getElementById(id)?.value) || 0;
  const stundenlohn = read('wg-stundenlohn');
  const lohngebundene = read('wg-lohngebundene');
  const gemeinkosten = read('wg-gemeinkosten');
  const pct = read('wg-zuschlag-pct');
  const ek = stundenlohn + lohngebundene + gemeinkosten;
  const zuschlagBetrag = (ek * pct) / 100;
  const vs = ek + zuschlagBetrag;
  const set = (id, txt) => { const el = document.getElementById(id); if (el) el.textContent = txt; };
  set('wg-stundenlohn-min', fmtMin(stundenlohn));
  set('wg-lohngebundene-min', fmtMin(lohngebundene));
  set('wg-gemeinkosten-min', fmtMin(gemeinkosten));
  set('wg-ek-h', fmtH(ek));
  set('wg-ek-min', fmtMin(ek));
  set('wg-zuschlag-h', fmtH(zuschlagBetrag));
  set('wg-zuschlag-min', fmtMin(zuschlagBetrag));
  set('wg-vs-h', fmtH(vs));
  set('wg-vs-min', fmtMin(vs));
}

// ── Aktions-Handler (gibt true zurück, wenn behandelt) ────
export function handleWageGroupAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  switch (a) {
    case 'wg-noop':
      return true;
    case 'wg-create-open':
      if (!ctx.can('wages:write')) return true;
      s.wgEdit = {
        open: true, id: null, name: '', isDefault: !(s.wageGroups || []).length,
        enablePreiskalkulation: true, enableNachkalkulation: false,
        stundenlohn: 0, lohngebundeneKosten: 0, gemeinkosten: 0, zuschlagProzent: 0,
        memberIds: [], saving: false, error: '', confirmDelete: false,
      };
      draw();
      return true;
    case 'wg-edit-open': {
      if (!ctx.can('wages:write')) return true;
      const g = (s.wageGroups || []).find((x) => x.id === el.dataset.id);
      if (!g) return true;
      s.wgEdit = {
        open: true, id: g.id, name: g.name, isDefault: g.isDefault,
        enablePreiskalkulation: g.enablePreiskalkulation, enableNachkalkulation: g.enableNachkalkulation,
        stundenlohn: g.stundenlohn, lohngebundeneKosten: g.lohngebundeneKosten,
        gemeinkosten: g.gemeinkosten, zuschlagProzent: g.zuschlagProzent,
        memberIds: [...(g.memberIds || [])], saving: false, error: '', confirmDelete: false,
      };
      draw();
      return true;
    }
    case 'wg-close':
      s.wgEdit = null;
      draw();
      return true;
    case 'wg-submit': {
      const e = s.wgEdit;
      if (!e) return true;
      const readMoney = (id) => Math.max(0, parseFloat(document.getElementById(id)?.value) || 0);
      const name = (document.getElementById('wg-name')?.value || '').trim();
      if (!name) { e.error = t('settings.wageGroups.nameRequired'); draw(); return true; }
      const payload = {
        name,
        memberIds: Array.from(document.querySelectorAll('[data-wg-member]:checked')).map((x) => x.value),
        enablePreiskalkulation: !!document.getElementById('wg-preis')?.checked,
        enableNachkalkulation: !!document.getElementById('wg-nach')?.checked,
        isDefault: !!document.getElementById('wg-default')?.checked,
        stundenlohn: readMoney('wg-stundenlohn'),
        lohngebundeneKosten: readMoney('wg-lohngebundene'),
        gemeinkosten: readMoney('wg-gemeinkosten'),
        zuschlagProzent: Math.max(0, parseFloat(document.getElementById('wg-zuschlag-pct')?.value) || 0),
      };
      e.saving = true; e.error = ''; draw();
      const req = e.id ? updateWageGroup(e.id, payload) : createWageGroup(payload);
      req
        .then(() => { s.wgEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.wgEdit) { s.wgEdit.saving = false; s.wgEdit.error = err.message || t('common.saveFailed'); } draw(); });
      return true;
    }
    case 'wg-delete': {
      const e = s.wgEdit;
      if (!e) return true;
      e.confirmDelete = true; draw();
      return true;
    }
    case 'wg-delete-cancel': {
      const e = s.wgEdit;
      if (!e) return true;
      e.confirmDelete = false; draw();
      return true;
    }
    case 'wg-delete-confirm': {
      const e = s.wgEdit;
      if (!e || !e.id) return true;
      e.saving = true; e.error = ''; draw();
      deleteWageGroup(e.id)
        .then(() => { s.wgEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.wgEdit) { s.wgEdit.saving = false; s.wgEdit.confirmDelete = false; s.wgEdit.error = err.message || t('common.deleteFailed'); } draw(); });
      return true;
    }
    default:
      return false;
  }
}
